import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { MMLImg } from 'components/MMLImg';
import { FocusKeys } from 'components/SpacialNavigation/focusKeys';
import MaterialButton from 'components/SpacialNavigation/MaterialButton';
import useFocusItem from 'hooks/useFocusItem';
import { getDeviceKeys } from 'hooks/useGoBack';
import { useSingleKeyComboTrigger } from 'hooks/useKeyComboTrigger';
import useUserConsent from 'hooks/useUserConsent';
import { logger } from 'logger';
import React, { useCallback, useEffect } from 'react';
import { asset } from 'utils/assets';
import classNames from 'utils/classNames';
import { Device, DeviceSubtype, getDevice, getDeviceSubtype } from 'utils/getDevice';
import { getVegaOsApi } from 'utils/vegaos/VegaOsApi';
import { Container, ModalContainer, NonClickableLink } from './styles';
import { useAppContext } from 'context/app-context';
import { tosPpStore } from 'apolloGraphql/mml-apollo-cache';
import { setValue } from 'utils/localStorage';
const backKey = getDeviceKeys().BACK;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOG = logger.getLogger('LegalTermsModal');

const LegalTermsModal = (): JSX.Element => {
  const { appConfig } = useAppContext();
  const { ackLegalTerms } = useUserConsent();
  const device = getDevice();
  const tosLink = appConfig?.web_endpoints.terms.href;
  const privacyLink = appConfig?.web_endpoints.privacy_policy.href;
  const supplementLink = appConfig?.web_endpoints.us_state_supplement.href;

  const { ref, focusKey, focusSelf, focused } = useFocusItem({
    focusKey: FocusKeys.LegalTermsModal,
    isFocusBoundary: true,
    focusable: true,
    preferredChildFocusKey: FocusKeys.LegalTermsModalAgree,
    disableScrollBehavior: true,
  });

  // If the user presses back while viewing the legal modal the app should exit
  const handleBackButton = useCallback(() => {
    switch (device) {
      case Device.androidtv:
      case Device.firetv: {
        if (getDeviceSubtype() === DeviceSubtype.vegaos) {
          getVegaOsApi()?.exitApp?.();
        } else {
          self.Android.confirmExitDialog();
        }
        break;
      }
      default: {
        LOG.error(
          `This device (${device}) does not support the custom exit dialog. This is likely an issue with this implementation since this should never be surfaced.`,
        );
      }
    }
  }, [device]);

  // We don't have a router to reference so we can't use the
  // normal useBackButton.  Listening to back key manually.
  useSingleKeyComboTrigger([backKey], handleBackButton);

  // agree callback
  const ackTerms = useCallback(() => {
    setValue('tosPpLocal', tosPpStore() + '');
    // This causes the app to reload
    ackLegalTerms();
  }, [ackLegalTerms]);

  // Focus modal
  useEffect(() => {
    focusSelf();
  }, [focusSelf]);

  return (
    <ModalContainer open={true} role={'main'}>
      <FocusContext.Provider value={focusKey}>
        <Container ref={ref} className={classNames({ 'nav-selected': focused })}>
          <MMLImg src={asset('/assets/appLogo_wide.png')} alt={'MML Logo'} height={38} width={461} />
          <div className="ConnH4Lvl5L">Legal Terms and Privacy</div>

          <div className="ConnBulletsLVL-5">
            {`By clicking "Agree", you have read and agree to our Terms of Use at (`}
            <NonClickableLink>{tosLink}</NonClickableLink>
            {`) and agree to the collection and use of your information by cookies and similar technologies, as set forth in the Privacy Policy at (`}
            <NonClickableLink>{privacyLink}</NonClickableLink>
            {`). Clicking "Agree" does not impact privacy choices you may have under state laws. Learn more in our US State Supplement (`}
            <NonClickableLink>{supplementLink}</NonClickableLink>
            {`).`}
          </div>

          <MaterialButton onClick={() => ackTerms()} focusableConfig={{ focusKey: FocusKeys.LegalTermsModalAgree }}>
            <div className="ConnButtonPrimaryLVL-5">AGREE</div>
          </MaterialButton>
        </Container>
      </FocusContext.Provider>
    </ModalContainer>
  );
};
export default LegalTermsModal;
